/**
 * useCheckInSession — fetch + SignalR-aware refetch for a single session.
 * Used by the Session Detail and Per-Device pages.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CheckInSession } from '@techgitter/contracts';

import { fetchCheckInSession } from '../services/checkInApi';
import { useSignalREvent } from './useSignalR';

export interface UseCheckInSessionResult {
  session: CheckInSession | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCheckInSession(sessionId: string | null): UseCheckInSessionResult {
  const [session, setSession] = useState<CheckInSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    fetchCheckInSession(sessionId, controller.signal)
      .then(data => {
        if (!controller.signal.aborted) setSession(data);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setError(err.message ?? 'Failed to load session');
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [sessionId, reloadKey]);

  const refresh = useCallback(() => setReloadKey(k => k + 1), []);

  // Refetch when the orchestrator or another tab broadcasts a change to
  // this session. Payload is an unknown union — match by sessionId.
  useSignalREvent('check_in_session:changed', (payload: unknown) => {
    if (!sessionId) return;
    if (typeof payload === 'object' && payload !== null) {
      const candidate = (payload as { sessionId?: string }).sessionId;
      if (candidate === sessionId) refresh();
    }
  });

  return { session, isLoading, error, refresh };
}
